import { IPROP_MAP, modifyProps } from '../../generators/modifyProps';
import { cloneElement, useState } from 'react';

import { JDSImageProps } from './generated';
import { Skeleton } from '../jdsskeleton/generated';

const legacyAspectRatios = [
  '1:1',
  'round',
  '4:3',
  '5:4',
  '16:9',
  '4:5',
  '3:4',
  '9:16',
  '2.75:1'
] as const;

type legacyAspectRatiosType = (typeof legacyAspectRatios)[number];

export interface ImageProps
  extends Omit<
    JDSImageProps,
    'aspectRatio' | 'provider' | 'onClick' | 'onLoad' | 'onError' | 'semantics'
  > {
  /**
   * This prop allows your app to pass your framework's `Image` element. Some `Image` libraries have props like `placeholder` and `fallback` etc. These props can be passed through the element sent to the provider prop
   * @example
   * ```
   * import { Image as NextImage} from "next/image";
   * <Image provider={<NextImage src='image.png'/>} />
   * ```
   */
  provider?: JSX.Element;
  /**
   * The aspect ratio of the image. `round` takes the `1:1` aspect ratio as default
   */
  aspectRatio?: legacyAspectRatiosType | JDSImageProps['aspectRatio'];
  alt?: string;
  onClick?: (ev: React.MouseEvent<HTMLDivElement>) => void;
  onError?: React.ImgHTMLAttributes<HTMLImageElement>['onError'];
  onLoad?: React.ImgHTMLAttributes<HTMLImageElement>['onLoad'];
}

export const PROP_MAP: IPROP_MAP<ImageProps> = (props) => {
  if (
    legacyAspectRatios.includes(props.aspectRatio as legacyAspectRatiosType)
  ) {
    console.warn(
      'these values for aspect ratios are deprectaed',
      legacyAspectRatios
    );
  }
  return {
    aspectRatio: {
      variations: {
        '1:1': {
          aspectRatio: 'square'
        },
        '4:3': {
          aspectRatio: 'horizontal_4_3'
        },
        '5:4': {
          aspectRatio: 'horizontal_5_4'
        },
        '16:9': {
          aspectRatio: 'horizontal_16_9'
        },
        '4:5': {
          aspectRatio: 'vertical_4_5'
        },
        '3:4': {
          aspectRatio: 'vertical_3_4'
        },
        '9:16': {
          aspectRatio: 'vertical_9_16'
        },
        '2.75:1': {
          aspectRatio: 'horizontal_2_75__1'
        }
      }
    },
    src: {
      properties: {
        src: props.src
      }
    },
    alt: {
      properties: {
        semantics: props.alt
      }
    }
  };
};

export const withLogic =
  (Component: React.ComponentType<ImageProps>): React.FC<ImageProps> =>
  (userProps: ImageProps) => {
    const props = modifyProps(userProps, PROP_MAP) as ImageProps;
    const { src, onLoad, onError, ...rest } = props;

    const [imageStates, setImageStates] = useState({
      error: false,
      loaded: false
    });

    const { error, loaded } = imageStates;

    const hasSrc = Boolean(src);
    const isBroken = hasSrc && loaded && error;

    const handleLoad = (e: React.SyntheticEvent<HTMLImageElement, Event>) => {
      setImageStates({ error: false, loaded: true });
      onLoad?.(e);
    };

    const handleError = (e: React.SyntheticEvent<HTMLImageElement, Event>) => {
      if (!imageStates.error) {
        setImageStates({ error: true, loaded: true });
        onError?.(e);
      }
    };

    const provider = () => {
      if (rest.provider) {
        const provider = rest.provider as React.HTMLProps<HTMLImageElement>;
        return cloneElement(rest?.provider, {
          onLoad: (e: React.SyntheticEvent<HTMLImageElement, Event>) => {
            handleLoad(e);
            provider?.onLoad?.(e);
          },
          onError: (e: React.SyntheticEvent<HTMLImageElement, Event>) => {
            handleError(e);
            provider?.onError?.(e);
          },
          src: rest.provider?.props?.src || src
        });
      }
      return null;
    };

    const currentState = !hasSrc
      ? 'empty'
      : isBroken
      ? 'error'
      : loaded
      ? 'loaded'
      : 'loading';
    const formatImageName = (imagePath = props.src || '') => {
      const parts = imagePath.split('/');
      const imageName = parts.length > 0 ? parts[parts.length - 1] : '';

      if (imageName) {
        const formattedName =
          imageName
            .replace(/\.\w+$/, '')
            .replace(/([A-Z])/g, ' $1')
            .toLowerCase() + ' image';
        return formattedName;
      }
      return 'Image name not found';
    };

    return (
      <Component
        {...{
          ...rest,
          skeleton: (
            <Skeleton
              shape='custom'
              animated={true}
              width='100%'
              __noCustomWarn__
            />
          ),
          _state: currentState,
          _loaderWidth: '100%',
          onLoad: handleLoad,
          onError: handleError,
          provider: provider() ?? undefined,
          _hasProvider: Boolean(rest.provider && currentState === 'loaded'),
          src: rest?.provider?.props?.src || src || {},
          ariaLabel: props.ariaLabel || formatImageName(props.src)
        }}
      />
    );
  };
